/**
 * WorkspaceWorkUnitPane — Generic GovernedWorkUnit inspector
 *
 * Surfaces workUnitId, workUnitHash, domain, unitType, scope, noTouch,
 * sourceRefs, actorRefs, artifactRefs, receiptRefs, attributionChain,
 * proofBoundary, requiredVerification, and unresolvedGaps.
 *
 * Gap extension v1: net-new pane. ImprovementPane covers improvement-intent
 * domain work units specifically; ProposalMemoryPane covers proposal
 * admissibilityStatus only. Neither covers the generic GovernedWorkUnit
 * inspector shape required across all domains (code, legal, personal, team).
 *
 * ImprovementPane remains intact as a domain-specific surface and is NOT
 * replaced or extended by this pane.
 *
 * SAFETY: Read-only, proof-only. No execution authority, no mutation.
 * Display does not imply recommendation, authorization, or legal conclusion.
 */

import React from 'react';
import type {
  WorkUnitPaneModel,
  AttributionChainEntry,
  WorkUnitStatus,
} from '../../modules/governanceEventStream/operationalRendering/types.js';
import { NEUTRAL, SPACING, TYPOGRAPHY, SEMANTIC } from '../../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface WorkspaceWorkUnitPaneProps {
  model: WorkUnitPaneModel;
  /** Emits the workUnitId when the identity row is clicked, propagating GWU selection to all panes. */
  onSelectWorkUnit?: (workUnitId: string) => void;
}

function statusColor(status: WorkUnitStatus) {
  if (status === 'ADMITTED' || status === 'COMPLETED') return SEMANTIC.stabilized;
  if (status === 'REJECTED') return SEMANTIC.refusal;
  return SEMANTIC.neutral;
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div style={{
      fontSize: TYPOGRAPHY.micro.size,
      fontWeight: 600,
      color: NEUTRAL.text500,
      textTransform: 'uppercase' as const,
      letterSpacing: 0.4,
      marginBottom: SPACING.sp2,
      marginTop: SPACING.sp3,
    }}>
      {title}
    </div>
  );
}

function RefPill({ value }: { value: string }) {
  return (
    <div style={{
      fontSize: TYPOGRAPHY.micro.size,
      fontFamily: TYPOGRAPHY.mono.family,
      color: NEUTRAL.text400,
      backgroundColor: NEUTRAL.bg700,
      padding: `${SPACING.sp1}px ${SPACING.sp2}px`,
      borderRadius: 2,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}>
      {value}
    </div>
  );
}

function TagPill({ value, intent }: { value: string; intent?: 'warn' }) {
  const bg = intent === 'warn' ? SEMANTIC.refusal.chip : NEUTRAL.bg600;
  const color = intent === 'warn' ? SEMANTIC.refusal.text : NEUTRAL.text400;
  return (
    <span style={{
      fontSize: TYPOGRAPHY.micro.size,
      color,
      backgroundColor: bg,
      padding: '2px 6px',
      borderRadius: 3,
      border: `1px solid ${NEUTRAL.borderSubtle}`,
    }}>
      {value}
    </span>
  );
}

function AttributionRow({ entry }: { entry: AttributionChainEntry }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: SPACING.sp2,
      padding: `${SPACING.sp1}px ${SPACING.sp2}px`,
      backgroundColor: NEUTRAL.bg700,
      borderRadius: 2,
      marginBottom: SPACING.sp1,
    }}>
      <span style={{
        fontSize: TYPOGRAPHY.micro.size,
        fontFamily: TYPOGRAPHY.mono.family,
        color: NEUTRAL.text300,
        flexShrink: 0,
        minWidth: 100,
      }}>
        {entry.timestamp.slice(0, 16).replace('T', ' ')}
      </span>
      <span style={{
        fontSize: TYPOGRAPHY.micro.size,
        fontWeight: 600,
        color: NEUTRAL.text500,
        flexShrink: 0,
        minWidth: 70,
      }}>
        {entry.role}
      </span>
      <span style={{
        fontSize: TYPOGRAPHY.micro.size,
        color: NEUTRAL.text400,
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {entry.actorRef}
      </span>
      <span style={{
        fontSize: TYPOGRAPHY.micro.size,
        color: NEUTRAL.text300,
        flexShrink: 0,
      }}>
        {entry.action}
      </span>
    </div>
  );
}

function EmptyNotice({ label }: { label: string }) {
  return (
    <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300, fontStyle: 'italic' }}>
      No {label}
    </div>
  );
}

export const WorkspaceWorkUnitPane: React.FC<WorkspaceWorkUnitPaneProps> = ({ model, onSelectWorkUnit }) => {
  if (model.error) {
    return (
      <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, fontSize: TYPOGRAPHY.body.size }}>
        Work unit pane error: {model.error}
      </div>
    );
  }

  const color = statusColor(model.status);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
        borderBottom: `1px solid ${NEUTRAL.border}`,
        backgroundColor: NEUTRAL.bg600,
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: TYPOGRAPHY.heading.size,
          fontWeight: TYPOGRAPHY.heading.weight,
          color: NEUTRAL.text600,
        }}>
          Work Unit
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sp2 }}>
          {model.domain && (
            <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text400 }}>
              {model.domain}
            </span>
          )}
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            fontWeight: 600,
            color: color.text,
            backgroundColor: color.chip,
            padding: '2px 7px',
            borderRadius: 3,
            textTransform: 'uppercase' as const,
            letterSpacing: 0.3,
          }}>
            {model.status}
          </span>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: SPACING.sp3 }}>

        {/* Safety strip */}
        <div style={{
          display: 'flex',
          gap: SPACING.sp2,
          flexWrap: 'wrap',
          marginBottom: SPACING.sp3,
          paddingBottom: SPACING.sp2,
          borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
        }}>
          {['READ-ONLY', 'PROOF-ONLY', 'NO EXECUTION AUTHORITY'].map(label => (
            <span key={label} style={{
              fontSize: TYPOGRAPHY.micro.size,
              fontWeight: 600,
              color: SEMANTIC.stabilized.text,
              backgroundColor: SEMANTIC.stabilized.chip,
              padding: '2px 6px',
              borderRadius: 3,
              textTransform: 'uppercase' as const,
              letterSpacing: 0.3,
            }}>
              {label}
            </span>
          ))}
        </div>

        {/* Identity */}
        {(model.workUnitId || model.workUnitHash) && (
          <>
            <SectionHeader title="Identity" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sp1 }}>
              {model.workUnitId && (
                <div style={{ display: 'flex', gap: SPACING.sp2, alignItems: 'center' }}>
                  <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300, minWidth: 60 }}>id</span>
                  <span
                    data-testid={`work-unit-id-${model.workUnitId}`}
                    onClick={() => onSelectWorkUnit?.(model.workUnitId!)}
                    style={{
                      fontSize: TYPOGRAPHY.caption.size,
                      fontFamily: TYPOGRAPHY.mono.family,
                      color: NEUTRAL.text500,
                      cursor: onSelectWorkUnit ? 'pointer' : 'default',
                      textDecoration: onSelectWorkUnit ? 'underline' : 'none',
                    }}
                  >
                    {model.workUnitId}
                  </span>
                </div>
              )}
              {model.workUnitHash && (
                <div style={{ display: 'flex', gap: SPACING.sp2, alignItems: 'center' }}>
                  <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300, minWidth: 60 }}>hash</span>
                  <span style={{ fontSize: TYPOGRAPHY.micro.size, fontFamily: TYPOGRAPHY.mono.family, color: NEUTRAL.text300 }}>
                    {model.workUnitHash.slice(0, 24)}…
                  </span>
                </div>
              )}
              {model.unitType && (
                <div style={{ display: 'flex', gap: SPACING.sp2, alignItems: 'center' }}>
                  <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300, minWidth: 60 }}>type</span>
                  <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text500 }}>{model.unitType}</span>
                </div>
              )}
              {model.proofBoundary && (
                <div style={{ display: 'flex', gap: SPACING.sp2, alignItems: 'center' }}>
                  <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300, minWidth: 60 }}>proof</span>
                  <span style={{ fontSize: TYPOGRAPHY.micro.size, fontFamily: TYPOGRAPHY.mono.family, color: NEUTRAL.text400 }}>
                    {model.proofBoundary}
                  </span>
                </div>
              )}
            </div>
          </>
        )}

        {/* Scope */}
        <SectionHeader title={`Scope (${model.scope.length})`} />
        {model.scope.length === 0 ? <EmptyNotice label="scope" /> : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING.sp1 }}>
            {model.scope.map((s, i) => <TagPill key={i} value={s} />)}
          </div>
        )}

        {/* noTouch */}
        <SectionHeader title={`No-Touch (${model.noTouch.length})`} />
        {model.noTouch.length === 0 ? <EmptyNotice label="no-touch" /> : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING.sp1 }}>
            {model.noTouch.map((s, i) => <TagPill key={i} value={s} intent="warn" />)}
          </div>
        )}

        {/* Source refs */}
        <SectionHeader title={`Source Refs (${model.sourceRefs.length})`} />
        {model.sourceRefs.length === 0 ? <EmptyNotice label="source refs" /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sp1 }}>
            {model.sourceRefs.map((r, i) => <RefPill key={i} value={r} />)}
          </div>
        )}

        {/* Actor refs */}
        <SectionHeader title={`Actor Refs (${model.actorRefs.length})`} />
        {model.actorRefs.length === 0 ? <EmptyNotice label="actor refs" /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sp1 }}>
            {model.actorRefs.map((r, i) => <RefPill key={i} value={r} />)}
          </div>
        )}

        {/* Artifact refs */}
        <SectionHeader title={`Artifact Refs (${model.artifactRefs.length})`} />
        {model.artifactRefs.length === 0 ? <EmptyNotice label="artifact refs" /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sp1 }}>
            {model.artifactRefs.map((r, i) => <RefPill key={i} value={r} />)}
          </div>
        )}

        {/* Receipt refs */}
        <SectionHeader title={`Receipt Refs (${model.receiptRefs.length})`} />
        {model.receiptRefs.length === 0 ? <EmptyNotice label="receipt refs" /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sp1 }}>
            {model.receiptRefs.map((r, i) => <RefPill key={i} value={r} />)}
          </div>
        )}

        {/* Attribution chain */}
        <SectionHeader title={`Attribution Chain (${model.attributionChain.length})`} />
        {model.attributionChain.length === 0 ? <EmptyNotice label="attribution chain" /> : (
          <div>
            {model.attributionChain.map((entry, i) => (
              <AttributionRow key={i} entry={entry} />
            ))}
          </div>
        )}

        {/* Required verification */}
        <SectionHeader title={`Required Verification (${model.requiredVerification.length})`} />
        {model.requiredVerification.length === 0 ? (
          <div style={{ fontSize: TYPOGRAPHY.micro.size, color: SEMANTIC.stabilized.text, fontStyle: 'italic' }}>
            No verification required
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING.sp1 }}>
            {model.requiredVerification.map((v, i) => <TagPill key={i} value={v} />)}
          </div>
        )}

        {/* Unresolved gaps */}
        <SectionHeader title={`Unresolved Gaps (${model.unresolvedGaps.length})`} />
        {model.unresolvedGaps.length === 0 ? (
          <div style={{ fontSize: TYPOGRAPHY.micro.size, color: SEMANTIC.stabilized.text, fontStyle: 'italic' }}>
            No unresolved gaps
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sp1 }}>
            {model.unresolvedGaps.map((g, i) => (
              <div key={i} style={{
                fontSize: TYPOGRAPHY.micro.size,
                color: SEMANTIC.refusal.text,
                backgroundColor: SEMANTIC.refusal.chip,
                padding: `${SPACING.sp1}px ${SPACING.sp2}px`,
                borderRadius: 2,
              }}>
                {g}
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};
